/**
 * SettingsPage
 * Kitchly v0.5.0
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  User,
  Mail,
  Lock,
  Globe,
  Info,
  LogOut,
  ChevronRight,
  Eye,
  EyeOff
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { auth } from '../services/api';

function SettingsPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', newPw: '', confirm: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwords.newPw !== passwords.confirm) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }

    if (passwords.newPw.length < 8) {
      toast.error(t('auth.passwordMinLength'));
      return;
    }

    setLoading(true);
    try {
      await auth.changePassword({
        current_password: passwords.current,
        new_password: passwords.newPw
      });
      toast.success(t('auth.passwordChanged'));
      setPasswords({ current: '', newPw: '', confirm: '' });
      setShowPasswordForm(false);
    } catch (err) {
      toast.error(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm(t('settings.logoutConfirm'))) {
      logout();
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>{t('settings.title')}</h1>
      </div>

      {/* Account Section */}
      <div className="card mb-lg">
        <h3 className="card-title mb-md">{t('settings.account')}</h3>

        <div className="list-item">
          <div className="list-item-icon" style={{ background: 'var(--color-primary-bg)', color: 'var(--color-primary)' }}>
            <User size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-subtitle">{t('settings.displayName')}</div>
            <div className="list-item-title">{user?.display_name}</div>
          </div>
        </div>

        <div className="list-item">
          <div className="list-item-icon" style={{ background: 'var(--color-secondary-light)', color: 'var(--color-secondary-dark)' }}>
            <Mail size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-subtitle">{t('settings.email')}</div>
            <div className="list-item-title">{user?.email}</div>
          </div>
        </div>

        <div
          className="list-item"
          onClick={() => setShowPasswordForm(!showPasswordForm)}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
        >
          <div className="list-item-icon" style={{ background: '#FFF3E0', color: 'var(--color-accent)' }}>
            <Lock size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-title">{t('settings.changePassword')}</div>
          </div>
          <ChevronRight size={18} color="var(--color-text-light)" />
        </div>
      </div>

      {/* Change Password Form */}
      {showPasswordForm && (
        <div className="card mb-lg">
          <h3 className="card-title mb-md">{t('settings.changePassword')}</h3>
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label className="form-label">{t('auth.currentPassword')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showCurrent ? 'text' : 'password'}
                  className="form-input"
                  value={passwords.current}
                  onChange={(e) => setPasswords(p => ({ ...p, current: e.target.value }))}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="btn-icon"
                  onClick={() => setShowCurrent(!showCurrent)}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                >
                  {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('auth.newPassword')}</label>
              <input
                type="password"
                className="form-input"
                value={passwords.newPw}
                onChange={(e) => setPasswords(p => ({ ...p, newPw: e.target.value }))}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('auth.confirmPassword')}</label>
              <input
                type="password"
                className="form-input"
                value={passwords.confirm}
                onChange={(e) => setPasswords(p => ({ ...p, confirm: e.target.value }))}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? t('common.loading') : t('common.save')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowPasswordForm(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Language Section */}
      <div className="card mb-lg">
        <div className="list-item">
          <div className="list-item-icon" style={{ background: 'var(--color-primary-bg)', color: 'var(--color-primary)' }}>
            <Globe size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-subtitle">{t('settings.language')}</div>
            <div className="list-item-title">English</div>
          </div>
          <span className="badge badge-primary">Phase 2</span>
        </div>
      </div>

      {/* About Section */}
      <div className="card mb-lg">
        <div className="list-item">
          <div className="list-item-icon" style={{ background: 'var(--color-bg-input)', color: 'var(--color-text-secondary)' }}>
            <Info size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-subtitle">{t('settings.about')}</div>
            <div className="list-item-title">{t('settings.version')} 0.5.0</div>
          </div>
        </div>
      </div>

      {/* Logout */}
      <button className="btn btn-danger btn-full btn-lg" onClick={handleLogout}>
        <LogOut size={20} />
        {t('settings.logout')}
      </button>
    </div>
  );
}

export default SettingsPage;
